/* eslint-disable react/jsx-props-no-spreading */
// import { useEffect } from 'react';
// import { useAppDispatch, useAppSelector } from '@doda/common/src/redux/hooks';
import type {
  JobDetailResponse,
  ScoutJobDetailResponse,
} from '@doda/api-client/aspida/@types';
import { Text } from '@doda/io-react/common';
import { makePickupImageAltText } from '@doda/common/src/logics/jobSearchDetail/makePickupImageAltText';
import { useAppSelector } from '@doda/common/src/redux/hooks';
import { abTestForManuscriptEGroupNoSelector } from '@doda/common/src/redux/features/abTest';
import { createJobProperty } from '../../logics/createJobProperty/createJobProperty';
import { useInitializeOnClient } from '../../hooks/useInitializeOnClient';
import {
  useImpressionAnalytics,
  useMeasureAbTestForManuscriptE,
} from '../../hooks/useImpressionAnalytics';
import { useScrollAnalytics } from '../../hooks/useScrollAnalytics';
import { PageLayout } from '../../components/PageLayout';
import { RouteTag } from '../../components/RouteTag';
import { MetaTag } from '../../components/MetaTag';
import { LinkTag } from '../../components/LinkTag';
import { BreadCrumbList } from '../../components/BreadcrumbList';
import { BreadCrumbListLd } from '../../components/BreadCrumbListLd';
import { JobPosting } from '../../components/JobPosting';
import { getHeadingProps, Heading } from '../../components/Heading';
import { ArticleSummary } from '../../components/ArticleSummary';
import { FollowHeaderDisplayArea } from '../../components/FollowHeader';
import { TabArea } from '../../components/TabArea';
import { MessageLink } from '../../components/MessageLink';
import { RelationInformation } from '../../components/RelationInformation';
import { RecommendSection } from '../../components/RecommendSection';
import { RelationLink } from '../../components/RelationLink';
import { createRelationLinkProps } from '../../components/RelationLink/logics/createRelationLinkProps';
import { SearchByDetailConditions } from '../../components/SearchByDetailConditions';
import type { Tab } from '../../types/tab';
import styles from './index.module.scss';

/**
 * JobSearchDetailのpageProps
 */
export interface PageProps {
  /** 求人詳細情報 */
  job: JobDetailResponse | ScoutJobDetailResponse;
  /** スカウト求人かどうか */
  isScout: boolean;
  /** 初期選択タブ */
  initialTab: Tab;
}

/**
 * 求人詳細画面コンポーネント
 */
export const JobSearchDetail = ({ job, isScout, initialTab }: PageProps) => {
  // const dispatch = useAppDispatch();
  // const canonicalUrl: string | null = useAppSelector(canonicalUrlSelector);
  useInitializeOnClient({
    jid: job.job?.jid || '',
    wmuid: job.job?.wmuid || '',
  });

  useImpressionAnalytics(job, isScout, initialTab);
  useScrollAnalytics(initialTab);
  useMeasureAbTestForManuscriptE(
    useAppSelector(abTestForManuscriptEGroupNoSelector),
    job.job?.wcnsId
  );

  // 変数定義
  const {
    jid,
    jobOfferKind,
    isApplied,
    isReachedLimit,
    wapid,
    wcnsId,
    recruitPrImage,
    relationInfoList,
    corporateClientId,
    isBookmarkSaved,
    hasDirectLink,
    directLink,
    isCanSent,
    scoutMessage,
    articleSummaryList,
    isRead,
  } = createJobProperty(job, isScout);
  const prImageList = recruitPrImage?.imagePathList || null;

  const showMessageLink = Boolean(scoutMessage?.url);

  const { isVisibleRelationLink, relationLinkProps } =
    createRelationLinkProps(job);

  return (
    <PageLayout job={job} isScout={isScout} initialTab={initialTab}>
      <RouteTag isScout={isScout} initialTab={initialTab} />
      <MetaTag job={job} isScout={isScout} initialTab={initialTab} />
      {prImageList && prImageList.length > 0 && (
        <LinkTag imageList={prImageList} initialTab={initialTab} />
      )}
      {!isScout && (
        <BreadCrumbList
          job={job}
          initialTab={initialTab}
          jid={jid}
          wcnsId={wcnsId}
        />
      )}
      <BreadCrumbListLd
        job={job}
        isScout={isScout}
        initialTab={initialTab}
        jid={jid}
        wcnsId={wcnsId}
      />
      <JobPosting
        jobSearchDetail={job}
        isScout={isScout}
        initialTab={initialTab}
        jid={jid}
        wcnsId={wcnsId || ''}
      />
      <Heading {...getHeadingProps(job, isScout, initialTab)} />
      {articleSummaryList.length !== 0 && (
        <div className={styles['jobSearchDetail-articleSummary']}>
          <ArticleSummary
            articleSummaryList={articleSummaryList}
            initialTab={initialTab}
          />
        </div>
      )}
      <FollowHeaderDisplayArea>
        <div className={styles['jobSearchDetail-tab']}>
          <TabArea
            wcnsId={wcnsId}
            initialTab={initialTab}
            jid={jid}
            applyParams={{
              directLink,
              hasDirectLink,
              isScout,
            }}
            jobOfferKind={jobOfferKind}
            isCanSent={isCanSent}
            isApplied={isApplied}
            askCompanyButtonProps={{
              isReachedLimit,
            }}
            pickupProps={{
              recruitPrImage,
              altText: job.job
                ? makePickupImageAltText(job.job, initialTab)
                : '',
              isScout,
              isRead,
            }}
            jobDetailProps={{ job, isScout }}
            favoriteButtonProps={{
              bookmarkListUrl: '/dcfront/bookmark/bookmarkList/',
              isBookmarked: isBookmarkSaved,
              wapid,
            }}
          />
        </div>
      </FollowHeaderDisplayArea>
      <div className={styles['jobSearchDetail-bodyBottom']}>
        {showMessageLink && (
          <MessageLink scoutMessage={scoutMessage} position="Footer" />
        )}
        <Text
          as="span"
          color="gray"
          size="11"
          weight="normal"
          className={styles['jobSearchDetail-bodyBottom__jobCode']}
        >
          求人コード {jid}
        </Text>
      </div>
      <div className={styles['jobSearchDetail-bottomSection']}>
        {!isScout &&
          corporateClientId &&
          relationInfoList &&
          relationInfoList.length !== 0 && (
            <RelationInformation
              initialRelationInfoList={relationInfoList}
              jid={jid}
              corporateClientId={corporateClientId}
            />
          )}
        <RecommendSection jid={jid} initialTab={initialTab} wcnsId={wcnsId} />
        {!isScout && isVisibleRelationLink && (
          <RelationLink
            initialTab={initialTab}
            jid={jid}
            wcnsId={wcnsId}
            relationLinkProps={relationLinkProps}
          />
        )}
        <SearchByDetailConditions
          initialTab={initialTab}
          jid={jid}
          wcnsId={wcnsId}
        />
      </div>
    </PageLayout>
  );
};
